/* ── The tab's section heading, in one place ─────────────────────────────────
 *
 * Every tab on this screen opens its sections the same way, and until 2 September 2026 all six
 * headings were written out by hand in four files — the same `motion.div`, the same `delay: 20`,
 * the same three classes, declared twice over in `DetailTab.module.css` and
 * `OverviewTab.module.css`. The two copies had already parted on one declaration (`flex: none`),
 * which is the whole argument for this file: nothing enforced that they agree.
 *
 * `flex: none` is `DetailTab`'s value and is what both get now. It is a no-op on today's screens —
 * measured 2 September 2026, every heading's used height equals its content height, so none was
 * being shrunk — and it is the honest declaration for a heading inside a scrolling column: a
 * heading that gives up height to its own section is a heading with a clipped descender. */

.head {
  display: flex;
  flex: none;
  gap: var(--space-9px);
  align-items: center;
  padding-bottom: var(--space-2px);
}

/* v3's `secHead` type. 17px/800 with a tight track, on a real `<h2>`, so a tab has a document
 * outline rather than six styled paragraphs. */
.title {
  margin: var(--space-0);
  font-size: var(--fs-detail-sec);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-detail-sec);
  white-space: nowrap;
}

/* How many things in this section want a reply. The accent's one spend on the tab, and it marks a
 * real state. `--accent-text` is the canon's own `emberText`; it stood at `--accent-ink`, a step
 * darkened for running text, until the owner withdrew the measured deviations on 1 September. */
.count {
  display: inline-grid;
  flex: none;
  place-items: center;
  min-width: var(--space-5);
  height: var(--space-5);
  padding: 0 var(--space-6px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--accent-text);
  background: var(--accent-soft);
  border-radius: var(--radius-7px);
  font-variant-numeric: tabular-nums;
}

/* The sentence at the far end. It takes the slack, so a long caption ellipsises rather than pushing
 * the heading out of the measure. */
.aside {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-align: end;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Where a heading carries a CONTROL at its far end rather than a sentence: the spacer takes the
 * slack so the control keeps its natural width instead of being stretched by a `flex: 1`. */
.spacer {
  flex: 1;
}
